import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import { ArrowRight, CheckCircle2, FileUp, ShieldCheck, Loader2 } from "lucide-react";
import { logger } from "../lib/logger";

const PROFESSIONAL_BODIES = [
  { value: "HPCSA", label: "HPCSA – Health Professions Council of SA" },
  { value: "SACSSP", label: "SACSSP – SA Council for Social Service Professions" },
  { value: "ASCHP", label: "ASCHP – Association for Supportive Counsellors & Holistic Practitioners" },
  { value: "OTHER", label: "Other regulated body" },
];

const MAX_CV_BYTES = 8 * 1024 * 1024; // 8MB — real CVs (with scans/photos) often run 5-7MB
const ALLOWED_CV_MIME = ["application/pdf", "image/jpeg", "image/png"];
const ALLOWED_CV_EXTS = [".pdf", ".jpg", ".jpeg", ".png"];

const inputCls = "w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none text-sm";
const labelCls = "text-xs font-bold uppercase tracking-[0.16em] text-[#3D405B] mb-1.5 block";
const Req = () => <span className="text-primary ml-0.5" aria-label="required" title="Required">*</span>;

export default function CounsellorApply() {
  const nav = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [cvFile, setCvFile] = useState(null); // {name, dataUrl}
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    professional_body: "HPCSA",
    registration_number: "",
    practice_mode: "both",
    years_experience: "",
    languages: "",
    specialties: "",
    bio: "",
    office_address: "",
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onCvSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Some browsers (esp. mobile Safari) return an empty file.type for PDFs from cloud
    // storage. Fall back to the filename extension so a legitimate CV isn't rejected.
    const nameLower = (file.name || "").toLowerCase();
    const extOk = ALLOWED_CV_EXTS.some((ext) => nameLower.endsWith(ext));
    const mimeOk = ALLOWED_CV_MIME.includes(file.type);
    if (!extOk && !mimeOk) {
      toast.error("Please upload a PDF, JPG, or PNG");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_CV_BYTES) {
      toast.error(`CV must be smaller than ${Math.round(MAX_CV_BYTES / 1024 / 1024)}MB`);
      e.target.value = "";
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCvFile({ name: file.name, dataUrl: reader.result });
      toast.success(`Attached ${file.name}`);
    };
    reader.onerror = () => toast.error("Could not read that file — please try a different one");
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.registration_number.trim()) {
      toast.error("Name, email and registration number are required");
      return;
    }
    if (!form.province || !form.city.trim() || !form.suburb.trim()) {
      toast.error("Province, city and suburb are all required");
      return;
    }
    setSubmitting(true);
    const payload = {
      name: form.name.trim(),
      email: form.email.trim().toLowerCase(),
      phone: form.phone.trim() || undefined,
      province: form.province,
      city: form.city.trim(),
      suburb: form.suburb.trim(),
      professional_body: form.professional_body,
      registration_number: form.registration_number.trim(),
      practice_mode: form.practice_mode,
      years_experience: form.years_experience === "" ? undefined : Number(form.years_experience),
      languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      specialties: form.specialties.split(",").map((s) => s.trim()).filter(Boolean),
      bio: form.bio.trim() || undefined,
      office_address: form.office_address.trim() || undefined,
      cv_document: cvFile?.dataUrl || undefined,
      cv_filename: cvFile?.name || undefined,
    };
    try {
      await api.post("/counsellor-apply", payload);
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit your application";
      toast.error(typeof msg === "string" ? msg : "Could not submit your application");
      logger.warn("counsellor-apply failed:", err?.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
          <div className="brand-card p-10 text-center" data-testid="counsellor-apply-success">
            <div className="w-16 h-16 rounded-full bg-secondary/15 text-secondary mx-auto mb-4 flex items-center justify-center">
              <CheckCircle2 size={32} />
            </div>
            <div className="overline mb-2">Application received</div>
            <h1 className="text-3xl font-extrabold text-[#3D405B] tracking-tight mb-3">Thank you — we&apos;ll be in touch.</h1>
            <p className="text-[#5F627D] leading-relaxed mb-6">
              Our clinical team will verify your registration with the professional body you nominated and respond within 3–5 business days. If approved, you&apos;ll get an email with instructions to set your password and go live in the platform.
            </p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all" data-testid="counsellor-apply-home-btn">
              Back home <ArrowRight size={16} />
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="mb-8">
          <div className="overline mb-2">Join our national network</div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-[#3D405B] tracking-tight">Apply as a counsellor</h1>
          <p className="text-[#5F627D] mt-3 max-w-2xl leading-relaxed">
            Are you a registered psychologist, counsellor, or social service professional in South Africa? Bring your practice online with Hellofoundation Youth Mental Wellness & Support. All applications are reviewed by our clinical team.
          </p>
        </div>

        <div className="brand-card p-5 mb-6 bg-accent/15 text-[#3D405B] text-sm flex items-start gap-3">
          <ShieldCheck size={18} className="text-secondary shrink-0 mt-0.5" />
          <div>
            <strong>Verification required.</strong> We&apos;ll check your registration against the public register of the body you nominate. Please make sure all details match exactly.
          </div>
        </div>

        <form onSubmit={submit} className="brand-card p-7 grid grid-cols-1 sm:grid-cols-2 gap-5" data-testid="counsellor-apply-form">
          <div className="sm:col-span-2 text-xs text-[#7A7D96]">
            Fields marked <span className="text-primary font-extrabold">*</span> are required.
          </div>
          <div>
            <label className={labelCls}>Full name<Req /></label>
            <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="apply-name" />
          </div>
          <div>
            <label className={labelCls}>Email<Req /></label>
            <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="apply-email" />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+27 …" data-testid="apply-phone" />
          </div>
          <div>
            <label className={labelCls}>Province<Req /></label>
            <select required value={form.province} onChange={set("province")} className={inputCls} data-testid="apply-province">
              <option value="">Select…</option>
              {["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape", "Free State", "Limpopo", "Mpumalanga", "North West", "Northern Cape"].map((p) => (<option key={p} value={p}>{p}</option>))}
            </select>
          </div>
          <div>
            <label className={labelCls}>City<Req /></label>
            <input required value={form.city} onChange={set("city")} className={inputCls} placeholder="Cape Town" data-testid="apply-city" />
          </div>
          <div>
            <label className={labelCls}>Suburb<Req /></label>
            <input required value={form.suburb} onChange={set("suburb")} className={inputCls} placeholder="Sea Point" data-testid="apply-suburb" />
          </div>
          <div>
            <label className={labelCls}>Professional body<Req /></label>
            <select value={form.professional_body} onChange={set("professional_body")} className={inputCls} data-testid="apply-body">
              {PROFESSIONAL_BODIES.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Registration number<Req /></label>
            <input required value={form.registration_number} onChange={set("registration_number")} className={inputCls} placeholder="e.g. PS 012 345 6" data-testid="apply-regnum" />
          </div>
          <div>
            <label className={labelCls}>Years of experience</label>
            <input type="number" min="0" max="60" value={form.years_experience} onChange={set("years_experience")} className={inputCls} placeholder="5" data-testid="apply-years" />
          </div>
          <div>
            <label className={labelCls}>Practice mode</label>
            <select value={form.practice_mode} onChange={set("practice_mode")} className={inputCls} data-testid="apply-mode">
              <option value="both">Online and in person</option>
              <option value="online">Online only</option>
              <option value="in_person">In person only</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Languages you offer sessions in (comma-separated)</label>
            <input value={form.languages} onChange={set("languages")} className={inputCls} placeholder="English, isiZulu, Afrikaans" data-testid="apply-languages" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Specialties (comma-separated)</label>
            <input value={form.specialties} onChange={set("specialties")} className={inputCls} placeholder="Anxiety, Trauma, Bullying, Family conflict" data-testid="apply-specialties" />
          </div>
          {(form.practice_mode === "in_person" || form.practice_mode === "both") && (
            <div className="sm:col-span-2">
              <label className={labelCls}>Office address (for in-person sessions)</label>
              <input value={form.office_address} onChange={set("office_address")} className={inputCls} placeholder="123 Main Rd, Rosebank, Johannesburg" data-testid="apply-office" />
            </div>
          )}
          <div className="sm:col-span-2">
            <label className={labelCls}>A short bio for your public profile</label>
            <textarea rows={4} value={form.bio} onChange={set("bio")} className={inputCls + " resize-none"} placeholder="A few sentences on your approach, training and who you love to work with." data-testid="apply-bio" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>CV or credentials (PDF / JPG / PNG · max 8MB)</label>
            <label className="flex items-center gap-3 w-full px-4 py-3 rounded-2xl bg-white border border-dashed border-[#3D405B]/25 hover:border-primary hover:bg-primary/5 cursor-pointer transition-all text-sm" data-testid="apply-cv-label">
              <FileUp size={18} className="text-primary" />
              <span className="text-[#3D405B] font-bold">{cvFile ? cvFile.name : "Choose file"}</span>
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={onCvSelect} className="hidden" data-testid="apply-cv-input" />
              {cvFile && <span className="ml-auto text-xs text-secondary font-bold">Attached ✓</span>}
            </label>
            <p className="text-xs text-[#7A7D96] mt-1.5">Optional but recommended — helps us verify you faster.</p>
          </div>

          <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-4 pt-2 border-t border-[#3D405B]/5">
            <div className="text-xs text-[#7A7D96] flex-1">
              By submitting you confirm the details above are accurate and consent to us verifying your registration with the body you nominated.
            </div>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed" data-testid="apply-submit">
              {submitting ? <><Loader2 size={16} className="animate-spin" /> Submitting…</> : <>Submit application <ArrowRight size={16} /></>}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
